import { z } from 'zod';
/**
 * Video insights: Gemini agentic video over a lab recording, producing a
 * PROPOSAL a human confirms. One job, four modes; only the prompt and the
 * result shape differ per mode.
 * Plan: docs/superpowers/plans/2026-09-07-video-insights-mvp.md
 */
export const VIDEO_INSIGHT_MODES = ['chapters', 'draft_protocol', 'enrich', 'deviations'];
/** `draft_protocol` needs no context; the other three need a protocol (today). */
export const videoInsightRequestSchema = z
    .object({
    mode: z.enum(VIDEO_INSIGHT_MODES),
    /** The artifact the video is read AGAINST. A protocol today; the seam for
     * notes/other types later is `resolveContext()` on the backend. */
    contextArtifactId: z.guid().optional(),
})
    .refine((r) => r.mode === 'draft_protocol' || !!r.contextArtifactId, {
    path: ['contextArtifactId'],
    error: 'contextArtifactId is required for this mode',
});
// Per-mode results ----------------------------------------------------------
export const chapterProposalSchema = z
    .object({
    stepId: z.string().min(1).max(120),
    startSeconds: z.number().min(0).max(86_400),
    endSeconds: z.number().positive().max(86_400),
    /** 0-1, the model's own confidence. Surfaced in the review UI, never gated on. */
    confidence: z.number().min(0).max(1).optional(),
})
    .refine((c) => c.endSeconds > c.startSeconds, {
    path: ['endSeconds'],
    error: 'endSeconds must be greater than startSeconds',
});
export const chaptersResultSchema = z.object({
    chapters: z.array(chapterProposalSchema).max(100),
    /** Steps the model said it could not find. Shown, never guessed. */
    notFoundStepIds: z.array(z.string()).max(100),
    /** Diagnostics from deterministic post-processing (rule 12: visible, not absorbed). */
    droppedOutOfRange: z.int().min(0),
    droppedOutOfOrder: z.int().min(0),
});
export const draftProtocolResultSchema = z.object({
    /**
     * Markdown the job turned into a new `protocol` artifact.
     *
     * 60,000 chars is roughly 15,000 tokens at this repo's 4-chars-per-token
     * convention, which is well UNDER the model's output budget — so this cap
     * is not sized to the budget and is not trying to be. A longer document is
     * REJECTED loudly here as a non-retryable schema failure rather than
     * silently truncated (rule 12); 60,000 is ~10x the length of any draft
     * observed, and exists as a sanity bound on what goes into artifact
     * metadata. If a real draft ever trips it, raise it deliberately — do not
     * add truncation.
     */
    markdown: z.string().min(1).max(60_000),
    createdArtifactId: z.guid().optional(),
});
export const enrichResultSchema = z.object({
    /** One entry per step the model had something to add for. Additive only:
     * the user copies what they want into the document. */
    suggestions: z
        .array(z.object({
        stepId: z.string().min(1).max(120),
        /** Markdown bullets: observed detail worth recording (equipment,
         * sequence, timing). Never a reagent/value claim (see prompt). */
        markdown: z.string().min(1).max(4000),
        evidenceSeconds: z.array(z.number().min(0)).max(10),
    }))
        .max(100),
    notVisible: z.array(z.string().max(300)).max(50),
    /**
     * Suggestions the parser threw away because the ITEM did not match the shape
     * above (rule 12: a per-item drop stays visible instead of being absorbed).
     * Mirrors `chaptersResultSchema`'s `droppedOutOfRange`/`droppedOutOfOrder`,
     * and is named for the same reason those are: the reason for the drop.
     *
     * Counts SHAPE failures only. A suggestion naming a step id that is not in
     * the protocol is dropped WITHOUT being counted here, exactly as an invented
     * step id is dropped uncounted from `chapters` — the model occasionally
     * invents one, and that filter is a deliberate, separate behaviour. If that
     * silence ever needs closing it should be closed for both shapes at once,
     * with its own counter; do not quietly fold it into this one.
     */
    droppedInvalid: z.int().min(0),
});
/**
 * The narrow deviation vocabulary. Deliberately NO kind for a wrong reagent,
 * volume, temperature or concentration: the benchmark demo read "50 mM NaOH"
 * as "1 M HCl", and a report that says so about someone's experiment is the
 * worst output this feature could produce. Ordering, presence and duration
 * are what the model demonstrably reads well (74.6 tIoU).
 *
 * NOT the same list as `DEVIATION_KINDS` in `execution.schema.ts`, and the
 * prefix is load-bearing rather than cosmetic. That one is the LIVE-AGENT
 * taxonomy and includes `wrong_reagent` and `parameter_off` — a voice/vision
 * agent is reporting what the user told it or what it watched in real time,
 * so a value claim there has a human behind it. Offline video has no such
 * check, which is why this vocabulary omits them. Never widen this list to
 * match that one.
 */
export const VIDEO_DEVIATION_KINDS = [
    'missing_step',
    'extra_action',
    'order_changed',
    'duration_anomaly',
];
export const deviationReportSchema = z.object({
    deviations: z
        .array(z.object({
        kind: z.enum(VIDEO_DEVIATION_KINDS),
        /** Absent for `extra_action` (no protocol step to point at). */
        stepId: z.string().min(1).max(120).optional(),
        description: z.string().max(500).optional(),
        confidence: z.number().min(0).max(1),
        evidenceSeconds: z.array(z.number().min(0)).max(10),
    }))
        .max(100),
    notVisible: z.array(z.string().max(300)).max(50),
    /** Deviations dropped for a malformed ITEM shape. See the note on
     * `enrichResultSchema.droppedInvalid`: shape failures only, and an unknown
     * `stepId` is filtered without being counted here. */
    droppedInvalid: z.int().min(0),
});
// Stored state on the VIDEO artifact: metadata.videoInsights[mode] ----------
export const VIDEO_INSIGHT_STATUSES = ['queued', 'ready', 'failed'];
/**
 * Every machine code `errorCode` may hold, closed. It is a `z.enum`, not a
 * bounded free string: the pipeline invents a code in five different files
 * (controller, context resolver, model call, parser, job) and renders them
 * through two user-facing message maps, and nothing but this list keeps those
 * in lockstep. A code that is not here must fail to COMPILE at the site that
 * assigns it, rather than reach a message map that has no entry for it and
 * silently degrade to the generic "reading the video failed".
 *
 * Grouped by the layer that raises it. `EMPTY_RESPONSE` is the one code that
 * is never STORED: the job converts it to `EMPTY_RESPONSE_EXHAUSTED` once the
 * retries are spent, and otherwise lets Cloud Tasks redeliver. It is in the
 * list because the model layer's own error type is a subset of this one.
 */
export const VIDEO_INSIGHT_ERROR_CODES = [
    // Request time (controller)
    'FEATURE_DISABLED',
    'VIDEO_NOT_READY',
    'NOT_A_VIDEO',
    'QUEUE_FAILED',
    /**
     * This deployment cannot address stored objects with a `gs://` URI (local
     * storage mode, no GCS bucket) — raised by the controller BEFORE accepting
     * the request, and re-checked in the job as defence in depth against a task
     * enqueued moments before that changed. Deterministic and known without
     * calling the model, so it is never retried.
     */
    'GCS_REQUIRED',
    // Context resolution
    'CONTEXT_NOT_FOUND',
    'CONTEXT_TYPE_UNSUPPORTED',
    'CONTEXT_HAS_NO_STEPS',
    // The model call
    'EMPTY_RESPONSE',
    'MAX_TOKENS',
    'ABORTED',
    'PROVIDER_FAILED',
    // Parsing the reply
    'PARSE_NO_JSON',
    'PARSE_SCHEMA',
    'PARSE_NO_HEADING',
    // Spend gates
    'QUOTA_EXHAUSTED',
    // The job, after exhausting what it can retry
    'EMPTY_RESPONSE_EXHAUSTED',
    'DELIVERY_ATTEMPTS_EXHAUSTED',
    'DRAFT_CREATE_FAILED',
    /**
     * The person who asked for this is no longer an active member of the
     * workspace (`jobs/queued-actor.guard.ts`). Stored rather than ACKed
     * silently: the controller writes `queued` BEFORE enqueuing, so a delivery
     * that bails without landing anything leaves that row saying `queued` for
     * ever — the mode's button stays disabled and the poller never stops.
     */
    'REQUESTER_INACTIVE',
    /**
     * `draft_protocol` only. A `protocol` may only hang off a workspace or a
     * collection (`ARTIFACT_TYPE_CONFIG.protocol.validParentTypes`), so a video
     * nested under a note has no legal parent to inherit that note's restriction
     * through. Rather than create the draft at the workspace root — where the
     * materialiser walks a chain with no restrictive ancestor and makes it
     * readable by every member — the request is landed as a failure.
     */
    'DRAFT_SOURCE_RESTRICTED',
    /**
     * Every mode that reads the video AGAINST a context artifact. The model's
     * answers quote and reference the context's steps, and they are stored on
     * the VIDEO row — so a context that fewer people may read than the video is
     * a broadening, which `rules/entity-acl.md` forbids ("a descendant can
     * narrow, never broaden"). The caller half is already closed by
     * `resolveContext`'s `assertCanRead`; this is the DESTINATION half, and the
     * two are independent because `videoArtifactId` is a path param while
     * `contextArtifactId` is a body field.
     */
    'CONTEXT_SOURCE_RESTRICTED',
];
export const videoInsightStateSchema = z.object({
    status: z.enum(VIDEO_INSIGHT_STATUSES),
    requestedAt: z.iso.datetime(),
    requestedByUserId: z.guid(),
    completedAt: z.iso.datetime().optional(),
    contextArtifactId: z.guid().optional(),
    /** `stepsSignature()` of the context steps the proposal was made against.
     * Lets the UI say "the protocol changed since this was proposed". */
    contextStepsSig: z.string().optional(),
    model: z.string().optional(),
    /** Closed machine code, never the provider's message (#630). */
    errorCode: z.enum(VIDEO_INSIGHT_ERROR_CODES).optional(),
    /** WARNING: `.default(0)` backfills on parse. Only parse FULL records with
     * this schema: validating a sparse PATCH through it silently resets a real
     * retry counter to 0. On Zod 4 `.partial()` keeps the default (an absent key
     * still parses to 0), so a patch schema must declare `attempts` WITHOUT a
     * default, e.g. `z.int().min(0).optional()`. */
    attempts: z.int().min(0).default(0),
    /** The MAP KEY is the discriminant, not a field inside the result: this is a
     * bare union, so read sites narrow by the `videoInsightsMetadata` key they
     * read from (`metadata.chapters?.result` is a `ChaptersResult`). The four
     * shapes are mutually exclusive at runtime — each has a distinct required
     * top-level key — so this is an ergonomics cost, not a safety one. Encoding
     * `mode` a second time inside each result would duplicate the map key; if
     * call sites multiply and the casting gets noisy, split this into per-mode
     * state types instead. */
    result: z
        .union([
        chaptersResultSchema,
        draftProtocolResultSchema,
        enrichResultSchema,
        deviationReportSchema,
    ])
        .optional(),
});
/**
 * A video usually has only one or two modes populated, so this map is partial
 * on purpose; `video-insight.schema.test.ts` pins `{}` parsing and unknown-key
 * rejection.
 */
export const videoInsightsMetadataSchema = z.partialRecord(z.enum(VIDEO_INSIGHT_MODES), videoInsightStateSchema);
/**
 * Stable, order-sensitive fingerprint of a step list. djb2 rather than crypto
 * so this module stays isomorphic for the frontend.
 *
 * The hash input is JSON rather than a delimiter-joined string because a
 * delimiter that can also occur INSIDE a field makes the boundary ambiguous:
 * `${id} ${title}` hashes `{ id: 'a b', title: 'cd' }` and
 * `{ id: 'a', title: 'b cd' }` to the same bytes. No hash width fixes that —
 * the encoding has to be unambiguous first. JSON quotes and escapes both
 * fields, so a boundary can never be forged from field content.
 *
 * The `${length}:` prefix is NOT part of that encoding fix and does not make
 * the encoding any less ambiguous — JSON already did. It earns its place by
 * PARTITIONING THE COLLISION DOMAIN: two step lists of different LENGTHS land
 * in different prefixes and so can never collide, no matter what the 32-bit
 * hash does. Adding or removing a step is the most common protocol edit and
 * exactly what this signature exists to notice, so that is the one case worth
 * taking off the table entirely rather than leaving to hash width.
 *
 * What a collision COSTS, so the stakes are on the record: `contextStepsSig`
 * gates one soft UI nudge — "the protocol changed since this was proposed".
 * A collision is therefore a MISSED WARNING, never a wrong answer and never a
 * bad write. Nothing downstream branches on it.
 */
export function stepsSignature(steps) {
    const s = JSON.stringify(steps.map((x) => [x.id, x.title]));
    let h = 5381;
    for (let i = 0; i < s.length; i++)
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return `${steps.length}:${(h >>> 0).toString(16)}`;
}
//# sourceMappingURL=video-insight.schema.js.map